import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useNavigate } from "react-router-dom";
import SEO from "../SEO/SEO"

const ServicesPage = () => {
  const navigate = useNavigate();
  const handleContact = () => navigate("/contact");
  const services = [
    {
      icon: "🏭",
      title: "Machinery Supply",
      description:
        "We provide high-quality industrial machinery including CNC machines, lathes, milling machines, and drilling equipment from trusted manufacturers.",
      features: [
        "Wide range of machinery options",
        "Quality assurance from manufacturers",
        "Competitive pricing",
        "Technical specifications guidance",
      ],
    },
    {
      icon: "🔩",
      title: "Industrial Parts Supply",
      description:
        "Comprehensive supply of genuine spare parts, bearings, motors, and mechanical components for various industrial applications.",
      features: [
        "Genuine spare parts",
        "Quick availability",
        "Multiple brand options",
        "Quality guaranteed components",
      ],
    },
    {
      icon: "⚙️",
      title: "Precision Tools Supply",
      description:
        "Supply of precision measuring instruments, cutting tools, and hand tools essential for industrial operations and maintenance.",
      features: [
        "High-precision tools",
        "Durable cutting instruments",
        "Accurate measuring devices",
        "Industry-standard quality",
      ],
    },
    {
      icon: "🚚",
      title: "Logistics & Delivery",
      description:
        "Efficient logistics support ensuring timely delivery of machinery and equipment to your location across India.",
      features: [
        "Pan-India delivery",
        "Safe packaging",
        "Timely shipping",
        "Installation support",
      ],
    },
    {
      icon: "🔧",
      title: "Technical Consultation",
      description:
        "Expert guidance in selecting the right machinery and equipment based on your specific industrial requirements.",
      features: [
        "Free technical consultation",
        "Machine selection guidance",
        "Specification analysis",
        "Budget optimization",
      ],
    },
    {
      icon: "📞",
      title: "After-Sales Support",
      description:
        "Reliable after-sales support including basic installation guidance and maintenance recommendations.",
      features: [
        "Installation guidance",
        "Maintenance tips",
        "Technical assistance",
        "Spare part identification",
      ],
    },
  ];
  const processSteps = [
    {
      step: "01",
      title: "Requirement Analysis",
      description: "We understand your specific machinery and equipment needs",
    },
    {
      step: "02",
      title: "Product Selection",
      description: "Help you choose the right machinery from our portfolio",
    },
    {
      step: "03",
      title: "Quotation & Pricing",
      description: "Provide competitive pricing and delivery timelines",
    },
    {
      step: "04",
      title: "Delivery & Support",
      description: "Ensure safe delivery and provide necessary support",
    },
  ];
  return (
    <>
      <Header />
      <SEO
        title="Services | Nexora Machinery"
        description="Learn about Nexora Machinery, a trusted supplier of CNC Turning Centers, VMC Machines, Milling Machines, Tapping Machines and industrial machinery."
        keywords="About Nexora Machinery, CNC Machine Supplier"
        path="/services"
      />
      <section className="relative bg-gradient-to-br from-[#002946] via-[#004e7c] to-[#0066a5] text-white py-20 lg:py-28 mt-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Our <span className="text-[#00d9ff]">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 font-light mb-8">
              Comprehensive Industrial Machinery & Trading Solutions
            </p>
            <div className="w-24 h-1 bg-[#00d9ff] mx-auto mb-8"></div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00d9ff] font-semibold text-lg">
              WHAT WE OFFER
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#002946] mt-2 mb-6">
              Our Comprehensive Services
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We provide end-to-end solutions for all your industrial machinery
              and equipment needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#00d9ff]/30"
              >
                <div className="text-5xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-[#002946] mb-4 group-hover:text-[#004e7c] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <div className="w-2 h-2 bg-[#00d9ff] rounded-full"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="absolute bottom-0 left-1/2 w-0 h-1 bg-gradient-to-r from-[#00d9ff] to-[#002946] group-hover:w-4/5 group-hover:left-1/10 transition-all duration-500 transform -translate-x-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00d9ff] font-semibold text-lg">
              OUR PROCESS
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#002946] mt-2 mb-6">
              How We Work
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Simple and efficient process to get you the right machinery and
              support
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#002946] to-[#004e7c] rounded-2xl flex items-center justify-center mx-auto text-white text-2xl font-bold">
                    {step.step}
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-gray-300 transform translate-x-1/2"></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#002946] mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-r from-[#002946] to-[#004e7c] text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get the Right Machinery?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us for expert consultation and competitive pricing on
            industrial machinery and equipment
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContact}
              className="bg-white text-[#002946] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
            >
              Get Free Consultation
            </button>
            {/* <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-[#002946] transition-all duration-300">
              Request Quote
            </button> */}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ServicesPage;
